import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  IconButton,
  LoaderWrapper,
  TableCell,
  TableCellEmail,
  TableWrapper,
} from '../../../services/settings/appStyle/global.style';
import { DeleteConfirmationModal, Loader, Table } from '../../../Components/uielements';
import { IntlMessages } from '../../../helper/langHelper';
import { DeleteIcon, EditIcon } from '../../../Components/icons';
import { listGroupMembers, removeGroup } from '../../../services/bioplus-services/group';
import GroupCreationModal from './modals/GroupCreationModal';
import ViewGroupMembers from './modals/viewGroupMembersModal';
import { useUserContext } from '../../../hooks/UserContext';

const Group = () => {
  const [deleteGroup, setDeleteGroup] = useState();
  const [editGroup, setEditGroup] = useState();
  const [groupMembers, setGroupMembers] = useState(null);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const userContext = useUserContext();

  const { userPermission } = userContext;
  const isGroupAllowToManage = userPermission?.find((permission) => permission?.label === 'group');

  // Permission flags derived from the "group" permission object
  const canManageGroup = !!isGroupAllowToManage?.isManage;

  const role = userContext?.company?.role;
  // const userType = role;
  const userType = role === 'EMPLOYEE' ? 'OWNER' : role;
  const companyId = userContext?.company?.companyId?._id;
  const groupType = 'FRIEND';

  const groupsQuery = useQuery(
    ['listGroupMembers', page, pageSize, userType, companyId, groupType],
    () => listGroupMembers({ page, pageSize, userType, companyId, groupType })
  );
  const groupListData = groupsQuery?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (group) => {
      return removeGroup({ groupId: group?._id });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries('listGroupMembers');
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message}`);
    },
  });

  const handleRemoveGroup = (group) => {
    deleteMutation.mutate(group);
    setDeleteGroup(undefined);
  };
  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const groupColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.groupName" defaultMessage="Group Name" />,
        accessor: 'name',
        Cell: (cellData) => <TableCell>{cellData?.row?.original?.name}</TableCell>,
        width: canManageGroup ? '20%' : '40%',
      },
      {
        Header: <IntlMessages id="table.col.members" defaultMessage="Members" />,
        accessor: 'userArray',
        width: '68%',
        Cell: (cellData) => {
          const members = cellData?.row?.original?.userArray || [];
          const displayMembers = members.slice(0, 3);
          const remainingCount = members.length - 3;

          return (
            <TableCellEmail
              onClick={() => setGroupMembers(cellData?.row?.original)}
              style={{ cursor: 'pointer', position: 'relative' }}
            >
              {displayMembers.map((member, index) => (
                <span key={member._id}>
                  {`${member.firstName} ${member.lastName}`}
                  {index < displayMembers.length - 1 && ', '}
                </span>
              ))}
              {remainingCount > 0 && (
                <span style={{ color: '#E6B471', marginLeft: '4px' }}>+{remainingCount} more</span>
              )}
            </TableCellEmail>
          );
        },
      },
    ];

    if (canManageGroup) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '12%',
      });
    }

    return cols;
  }, [canManageGroup]);

  const actionList = [
    ...(canManageGroup
      ? [
          (data) => (
            <IconButton onClick={() => setEditGroup(data)}>
              <EditIcon style={{ width: '22px', height: '22px' }} />
            </IconButton>
          ),
          (data) => (
            <IconButton onClick={() => setDeleteGroup(data)}>
              <DeleteIcon style={{ width: '22px', height: '22px' }} />
            </IconButton>
          ),
        ]
      : []),
  ];

  if (groupsQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <TableWrapper>
        <Table
          data={groupListData}
          columns={groupColumns}
          actions={actionList}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={groupsQuery?.data?.total || 0}
        />
      </TableWrapper>

      {deleteGroup && (
        <DeleteConfirmationModal
          onClose={() => setDeleteGroup(undefined)}
          message={
            <IntlMessages
              id="group.delete.confirmation"
              defaultMessage={`Are you sure you want to delete the group "${deleteGroup?.name}"?`}
              values={{ group: deleteGroup?.name }}
            />
          }
          onConfirm={() => handleRemoveGroup(deleteGroup)}
        />
      )}

      {groupMembers && (
        <ViewGroupMembers onClose={() => setGroupMembers(null)} groupMembers={groupMembers} />
      )}

      {editGroup && (
        <GroupCreationModal
          onClose={() => setEditGroup(null)}
          groupData={editGroup}
          isEdit={true}
        />
      )}
    </>
  );
};

export default Group;
